import type {
  CartLineLikeForAnalytics,
  EcommerceItem,
  OrderLineLikeForAnalytics,
  ProductLikeForAnalytics,
  VariantLikeForAnalytics,
} from './ecommerce-types';
import { parseVariationAttributesForAnalytics } from './variant-attributes';

/** Convert cart/API cents to decimal currency units for GA4 value fields. */
export function centsToEcommerceValue(cents: number): number {
  return Math.round(cents) / 100;
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const out = { ...obj };
  for (const key of Object.keys(out)) {
    if (out[key] === undefined) delete out[key];
  }
  return out;
}

function lineDiscountFromCompare(unitCents: number, compareCents?: number): number | undefined {
  if (compareCents == null || compareCents <= unitCents) return undefined;
  return centsToEcommerceValue(compareCents - unitCents);
}

function productImageUrl(product: ProductLikeForAnalytics): string | undefined {
  const first = product.images?.[0];
  return first?.url ?? first?.src;
}

export function buildEcommerceItemFromProductVariant(
  product: ProductLikeForAnalytics,
  variant: VariantLikeForAnalytics,
  quantity = 1,
  options?: { itemListName?: string; itemListId?: string; index?: number },
): EcommerceItem {
  const unitCents = variant.price.display;
  const compareCents = variant.compare_at_price?.display;
  const groupProductId = product.uuid;
  const attrDims = parseVariationAttributesForAnalytics(variant.variation_attributes);

  return stripUndefined({
    item_id: groupProductId,
    item_group_id: groupProductId,
    item_variant_id: variant.id,
    item_name: product.title,
    item_variant: variant.title || variant.id,
    item_color: attrDims.item_color,
    item_size: attrDims.item_size,
    item_sku: variant.sku,
    item_category: product.tags?.[0],
    item_category2: product.tags?.[1],
    item_brand: product.vendor,
    item_list_name: options?.itemListName,
    item_list_id: options?.itemListId ?? product.slug,
    index: options?.index,
    price: centsToEcommerceValue(unitCents),
    quantity,
    image_url: productImageUrl(product),
    discount: lineDiscountFromCompare(unitCents, compareCents),
    compare_at_price: compareCents != null ? centsToEcommerceValue(compareCents) : undefined,
  }) as EcommerceItem;
}

export function buildEcommerceItemFromCartLine(
  line: CartLineLikeForAnalytics,
  options?: { itemListName?: string; index?: number },
): EcommerceItem {
  const lineTotalCents = line.price.display;
  const unitCents = line.quantity > 0 ? Math.round(lineTotalCents / line.quantity) : lineTotalCents;
  const compareUnitCents =
    line.compare_at_price && line.quantity > 0
      ? Math.round(line.compare_at_price.display / line.quantity)
      : line.compare_at_price?.display;

  return stripUndefined({
    item_id: line.product_id,
    item_group_id: line.product_id,
    item_variant_id: line.variant_id,
    item_name: line.name,
    item_variant: line.variant_id,
    item_sku: line.sku,
    item_list_name: options?.itemListName,
    index: options?.index,
    price: centsToEcommerceValue(unitCents),
    quantity: line.quantity,
    image_url: line.image_url,
    discount: lineDiscountFromCompare(unitCents, compareUnitCents),
    compare_at_price:
      compareUnitCents != null ? centsToEcommerceValue(compareUnitCents) : undefined,
  }) as EcommerceItem;
}

export function buildEcommerceItemFromOrderLine(
  line: OrderLineLikeForAnalytics,
  options?: { index?: number },
): EcommerceItem {
  const unitCents = line.unitPriceDisplayCents;

  return stripUndefined({
    item_id: line.productId,
    item_group_id: line.productId,
    item_variant_id: line.variantId,
    item_name: line.name,
    item_variant: line.variantId,
    item_sku: line.sku,
    item_category: line.category,
    index: options?.index,
    price: centsToEcommerceValue(unitCents),
    quantity: line.quantity,
    image_url: line.image_url,
  }) as EcommerceItem;
}

export function sumEcommerceValueFromItems(items: EcommerceItem[]): number {
  return items.reduce((sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1), 0);
}
